import { useBillingServiceStore } from '@billing/AppProvider'
import { TextField } from '@mui/material'
import { Stack } from '@mui/system'
import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { CatalogueDialogMessage } from '../components/atoms/CatalogueDialogMessage'
import { CatelogUploadDialog } from '../components/molecules/CatelogueUploadDialog'
import { CatalogueFormProfile } from '../models/catalog.model'

export const CatalogueDialogContainer = observer(() => {
  const { t } = useTranslate()
  const { appStore, catalogueDialogUIStore } = useBillingServiceStore()
  const { selectedCatalogue } = catalogueDialogUIStore

  const { control, handleSubmit, reset } = useForm<CatalogueFormProfile>({
    defaultValues: {
      id: undefined,
      name: '',
    },
  })

  useEffect(() => {
    reset(selectedCatalogue)
  }, [reset, selectedCatalogue])

  const handleCloseCreate = () => {
    catalogueDialogUIStore.toggleCreate()
    reset({ name: '' })
  }

  const onSubmit = async (data: CatalogueFormProfile) => {
    await catalogueDialogUIStore.onSubmit(data)
    handleCloseCreate()
    if (selectedCatalogue) {
      appStore.setShowAlert('success', t('catalogueRenamed', 'Catalogue renamed successfully.'))
    } else {
      appStore.setShowAlert('success', t('catalogueAdded', 'Catalogue added successfully.'))
    }
  }

  const handleCloseDelete = () => {
    catalogueDialogUIStore.toggleDelete()
  }
  const handleDelete = async () => {
    await catalogueDialogUIStore.delete()
    appStore.setShowAlert('success', t('catalogueAdded', 'Catalogue added successfully.'))
    handleCloseDelete()
  }

  return (
    <>
      <ConfirmationDialog
        open={catalogueDialogUIStore.isCreateOpen}
        formId='catalogue-form'
        title={
          selectedCatalogue
            ? t('renameCatalogue', 'Rename catalogue')
            : t('createCatalogue', 'Create catalogue')
        }
        primaryActionText={t('save', 'Save')}
        closeLabel={t('cancel', 'Cancel')}
        onClose={handleCloseCreate}
        maxWidth='xs'
      >
        <Stack
          component='form'
          id='catalogue-form'
          onSubmit={handleSubmit(onSubmit)}
          gap={2}
          sx={{ width: '100%' }}
        >
          <Controller
            name='name'
            control={control}
            rules={{ required: t('nameRequired', 'Name is required') }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={t('name', 'Name')}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                autoFocus
                fullWidth
              />
            )}
          />
        </Stack>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={catalogueDialogUIStore.isDeleteOpen}
        title={t('deleteCatalogue', 'Delete catalogue "{name}"?', {
          name: catalogueDialogUIStore.selectedCatalogue?.name || '',
        })}
        message={
          <CatalogueDialogMessage
            name={catalogueDialogUIStore.selectedCatalogue?.name || ''}
            quantity={
              catalogueDialogUIStore.selectedCatalogue?.id &&
              catalogueDialogUIStore.catalogueQuantityMap.get(
                catalogueDialogUIStore.selectedCatalogue?.id,
              )
            }
          />
        }
        primaryActionText={t('yesDelete', 'Yes, delete')}
        closeLabel={t('cancel', 'Cancel')}
        maxWidth='xs'
        blockClickOutside
        onConfirm={handleDelete}
        onClose={handleCloseDelete}
      />
      <CatelogUploadDialog dialogStore={catalogueDialogUIStore} />
    </>
  )
})
